import { useState, useRef, useLayoutEffect, useEffect, type ReactNode } from 'react';
import Resizer from '@rio-cloud/rio-uikit/Resizer';
import { hasPaddingClass } from '@rio-cloud/rio-uikit/utils/hasUtilityClass';
import classNames from '@rio-cloud/rio-uikit/classNames';

import { ErrorBoundary } from './ErrorBoundary';
import { CodeSnippets } from '../CodeSnippets';
import { ReportDialog } from '../ReportDialog';
import { PlaygroundDetachDialog, type PlaygroundReportContext } from '../../detach/PlaygroundDetachDialog';
import { PlaygroundHeader } from './PlaygroundHeader';
import { findByName } from '../../utils/mapUtils';
import { breakpoints, getBreakpointNames } from '../../utils/breakpoints';
import { mergeClassNameOverrides } from '@rio-cloud/rio-uikit/mergeClassNameOverrides';

export const PLAYGROUNG_BG_WHITE = 'bg-white';
export const PLAYGROUNG_BG_LIGHTEST = 'bg-lightest';
export const PLAYGROUNG_BG_LIGHTER = 'bg-lighter';
export const PLAYGROUNG_BG_LIGHT = 'bg-light';
export const PLAYGROUNG_BG_CHECKERBOARD = 'bg-checkerboard';

const INITIAL_RESIZER_WIDTH = 100;
const DEFAULT_MIN_PLAYGROUND_WIDTH = 400;
const MOBILE_BREAKPOINT = 480;
const PLAYGROUND_FEEDBACK_URL = 'https://my-rio.slack.com/archives/C8T2WSZJ9';

const MOBILE_CONTAINER_CLASS = 'container-query-mobile';

const eachBreakpoint = getBreakpointNames(breakpoints);
const eachBreakpointWithoutZero = eachBreakpoint.slice(1);

const pxToNumber = (value = '0') => Number(value.replace('px', ''));

type PlaygroundSize = 'phone' | 'desktop' | 'width';

type PlaygroundProps = {
    mcpVisualId?: string;
    size?: PlaygroundSize;
    minPlaygroundWidth?: number;
    initialPlaygroundWidth?: number;
    disableMobileContainerClass?: boolean;
    background?: string;
    className?: string;
    innerClassName?: string;
    example?: ReactNode;
    htmlExampleElement?: HTMLElement | null;
    codeHtml?: string;
    codeHtmlTitle?: string;
    codeReact?: string;
    codeReactTitle?: string;
    codeJS?: string;
    codeJSTitle?: string;
    props?: ReactNode;
    openBlock?: 'codeReact' | 'codeHtml' | 'codeJS' | 'props' | 'classes';
    showHtml?: boolean;
    showFeedbackButton?: boolean;
    showSizeButtons?: boolean;
    showBackgroundButtons?: boolean;
    classes?: ReactNode;
    availableColors?: ReactNode;
    children?: ReactNode;
};

const getSectionUrl = (anchorId: string) => {
    const routePath = window.location.hash.slice(1).split('#')[0];

    if (!routePath) {
        return `${window.location.origin}${window.location.pathname}${window.location.search}#${anchorId}`;
    }

    return `${window.location.origin}${window.location.pathname}${window.location.search}#${routePath}#${anchorId}`;
};

const getPlaygroundReportContext = (playgroundElement: HTMLElement | null): PlaygroundReportContext => {
    const pageUrl = window.location.href;

    if (!playgroundElement) {
        return { pageUrl };
    }

    const previousDescriptions = [...document.querySelectorAll<HTMLElement>('.description-headline')].filter(
        description => (description.compareDocumentPosition(playgroundElement) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
    );
    const sectionDescription = previousDescriptions.at(-1);

    if (!sectionDescription) {
        return { pageUrl };
    }

    const sectionAnchor = sectionDescription.parentElement?.querySelector<HTMLAnchorElement>(':scope > .anchor[id]');
    const sectionUrl = sectionAnchor?.id ? getSectionUrl(sectionAnchor.id) : undefined;

    return {
        pageUrl,
        sectionUrl,
    };
};

export const Playground = (props: PlaygroundProps) => {
    const {
        size,
        mcpVisualId,
        minPlaygroundWidth = DEFAULT_MIN_PLAYGROUND_WIDTH,
        initialPlaygroundWidth,
        disableMobileContainerClass = false,
        background = 'bg-white',
        className = '',
        innerClassName = '',
        showHtml = true,
        showFeedbackButton = true,
        openBlock,
        example,
        htmlExampleElement,
        props: componentProps,
        codeHtml,
        codeHtmlTitle = 'HTML',
        codeReact,
        codeReactTitle = 'React',
        codeJS,
        codeJSTitle = 'JavaScript',
        classes,
        availableColors,
        children,
        showSizeButtons = true,
        showBackgroundButtons = true,
    } = props;

    const [backgroundClass, setBackgroundClass] = useState(background);
    const [playgroundSize, setPlaygroundSize] = useState(size);
    const [isDOMReady, setIsDOMReady] = useState(false);
    const [showResizer, setShowResizer] = useState(false);
    const [playgroundWidth, setPlaygroundWidth] = useState<number | string>('100%');
    const [isResizing, setIsResizing] = useState(false);
    const [showDetachedDialog, setShowDetachedDialog] = useState(false);
    const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
    const [reportContext, setReportContext] = useState<PlaygroundReportContext | null>(null);

    const contentRef = useRef<HTMLDivElement>(null);
    const playgroundRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        setIsDOMReady(true);
    }, []);

    useEffect(() => {
        if (size) {
            handlePlaygroundSize(size);
        }
    }, [initialPlaygroundWidth, minPlaygroundWidth, size]);

    const toggleMobileContainerClass = (playgroundTargetWidth: number) => {
        if (disableMobileContainerClass) {
            contentRef.current?.classList.remove(MOBILE_CONTAINER_CLASS);
            return;
        }

        const playgroundClasses = contentRef.current?.className;

        if (playgroundTargetWidth > MOBILE_BREAKPOINT && playgroundClasses?.includes(MOBILE_CONTAINER_CLASS)) {
            contentRef.current?.classList.remove(MOBILE_CONTAINER_CLASS);
        } else if (playgroundTargetWidth <= MOBILE_BREAKPOINT && !playgroundClasses?.includes(MOBILE_CONTAINER_CLASS)) {
            contentRef.current?.classList.add(MOBILE_CONTAINER_CLASS);
        }
    };

    const handleBackgroundChange = (newBg: string) => setBackgroundClass(newBg);

    const handlePlaygroundSize = (newSize: PlaygroundSize) => {
        setIsResizing(false);

        switch (newSize) {
            case 'width': {
                const scrollWidth = playgroundRef.current?.scrollWidth || 0;
                const targetWidth = Math.max(
                    initialPlaygroundWidth ?? scrollWidth - INITIAL_RESIZER_WIDTH,
                    minPlaygroundWidth
                );
                toggleResizer(true);
                setPlaygroundWidth(targetWidth);
                toggleMobileContainerClass(targetWidth);
                break;
            }
            case 'phone':
                toggleResizer(true);
                setPlaygroundWidth(MOBILE_BREAKPOINT);
                toggleMobileContainerClass(MOBILE_BREAKPOINT);
                break;
            default:
                toggleResizer(false);
                setPlaygroundWidth('100%');
                break;
        }
        setPlaygroundSize(newSize);
    };

    const toggleResizer = (shouldShow: boolean) => {
        if (shouldShow) {
            setPlaygroundWidth(playgroundRef.current?.scrollWidth || 0);
        } else {
            setPlaygroundWidth('100%');
        }
        setShowResizer(shouldShow);
    };

    const handleResize = (diff: number) => {
        setPlaygroundWidth(oldWidth => {
            const currentPlaygroundWidth = playgroundRef.current?.scrollWidth || 0;

            const updatedWidth = playgroundWidth === '100%' ? currentPlaygroundWidth : (oldWidth as number) - diff;
            const clampedWidth = updatedWidth <= minPlaygroundWidth ? minPlaygroundWidth : updatedWidth;

            toggleMobileContainerClass(clampedWidth);

            return clampedWidth;
        });
    };

    const handleDetach = () => {
        setReportContext(getPlaygroundReportContext(playgroundRef.current));
        setShowDetachedDialog(true);
    };

    const handleFeedback = () => {
        setReportContext(getPlaygroundReportContext(playgroundRef.current));
        setShowFeedbackDialog(true);
    };

    const content = example || children;
    const feedbackUrl = reportContext?.sectionUrl ?? reportContext?.pageUrl ?? window.location.href;
    const feedbackReportMessage = [
        'Feedback',
        feedbackUrl,
        mcpVisualId ? `Visual ID: ${mcpVisualId}` : null,
        '',
        'What is broken, missing, or unclear?',
        '- ',
    ]
        .filter((line): line is string => line !== null)
        .join('\n');

    const wrapperClasses = mergeClassNameOverrides(
        classNames(
            'playground shadow-default rounded border margin-bottom-50 overflow-hidden',
            playgroundSize && `size-${playgroundSize}`
        ),
        className
    );

    const playgroundContentClasses = classNames(
        'playground-content',
        innerClassName,
        backgroundClass,
        isResizing && 'user-select-none',
        !hasPaddingClass(innerClassName) ? 'padding-20 padding-bottom-25' : ''
    );

    return (
        <div className={wrapperClasses} data-mcp-visual-id={mcpVisualId} ref={playgroundRef}>
            {!content && (
                <CodeSnippets
                    codeReact={codeReact}
                    codeJS={codeJS}
                    openBlock={openBlock}
                    componentProps={props.props}
                />
            )}
            {content && (
                <>
                    <PlaygroundHeader
                        availableColors={availableColors}
                        onBackgroundChange={handleBackgroundChange}
                        onFeedback={handleFeedback}
                        showSizeButtons={showSizeButtons}
                        showFeedbackButton={showFeedbackButton}
                        showBackgroundButtons={showBackgroundButtons}
                        onPlaygroundSizeChange={handlePlaygroundSize}
                        onDetach={handleDetach}
                    />
                    <ErrorBoundary>
                        <div className='display-flex height-100pct'>
                            <div
                                className={playgroundContentClasses}
                                style={{ width: playgroundWidth }}
                                ref={contentRef}
                            >
                                {content}
                                {showResizer && (
                                    <Resizer
                                        className={
                                            'right-0 display-grid place-items-center width-auto height-100pct ' +
                                            'padding-2 bg-white border border-top-none border-bottom-none'
                                        }
                                        position={Resizer.RIGHT}
                                        onResizeStart={() => setIsResizing(true)}
                                        onResize={handleResize}
                                        onResizeEnd={() => setIsResizing(false)}
                                    >
                                        <div className='height-20 width-2 bg-gray' />
                                    </Resizer>
                                )}
                            </div>
                            <div className='playground-sizes'>
                                {eachBreakpointWithoutZero.map(breakpointName => {
                                    const breakpoint = findByName(breakpointName, breakpoints);

                                    if (!breakpoint) {
                                        return null;
                                    }

                                    const { minWidth, prevBreakpointSize } = breakpoint;

                                    return (
                                        <div
                                            key={`index-${prevBreakpointSize}`}
                                            style={{
                                                minWidth: pxToNumber(minWidth) - pxToNumber(prevBreakpointSize),
                                            }}
                                            className={
                                                'padding-10 border border-style-dashed border-color-light ' +
                                                'border-right-only text-color-gray'
                                            }
                                        >
                                            <div>
                                                Width: <b>{prevBreakpointSize}</b>
                                            </div>
                                            {prevBreakpointSize === '480px' && (
                                                <>
                                                    <p>
                                                        This demo emulates the mobile breakpoint to demonstrate how to
                                                        handle mobile screen size.
                                                    </p>
                                                    <hr />
                                                    <div className='text-size-12 opacity-70 line-height-125rel'>
                                                        <p>
                                                            To handle various screen sizes, please use the
                                                            breakpoint-specific responsive CSS classes. Many components
                                                            have them built-in already. In some cases, you need to set
                                                            them individually.
                                                        </p>
                                                        <p>
                                                            These classes are documented in the CLASS expander of the
                                                            respective aspect in the Design section.
                                                        </p>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </ErrorBoundary>
                    {isDOMReady && (
                        <CodeSnippets
                            exampleRef={contentRef}
                            exampleElement={htmlExampleElement}
                            showHtml={showHtml}
                            codeHtml={codeHtml}
                            codeHtmlTitle={codeHtmlTitle}
                            codeReact={codeReact}
                            codeReactTitle={codeReactTitle}
                            codeJS={codeJS}
                            codeJSTitle={codeJSTitle}
                            componentProps={componentProps}
                            classes={classes}
                            openBlock={openBlock}
                        />
                    )}
                    <PlaygroundDetachDialog
                        contentClassName={playgroundContentClasses}
                        reportContext={reportContext}
                        show={showDetachedDialog}
                        onClose={() => setShowDetachedDialog(false)}
                    >
                        {content}
                    </PlaygroundDetachDialog>
                    <ReportDialog
                        show={showFeedbackDialog}
                        title='Feedback'
                        subtitle='Copy the report and post it in Slack.'
                        bodyIntro='Describe what is broken, missing, or unclear so we can improve it.'
                        initialValue={feedbackReportMessage}
                        linkButtonHref={PLAYGROUND_FEEDBACK_URL}
                        linkButtonLabel='Open Slack'
                        onClose={() => setShowFeedbackDialog(false)}
                    />
                </>
            )}
        </div>
    );
};
